import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, Clock, Target, CheckCircle, AlertTriangle, 
  TrendingUp, Users, DollarSign, MapPin, ChevronRight,
  ChevronLeft, Plus, Filter, Download, RefreshCw,
  BarChart3, List, Grid, MoreHorizontal, Play, Pause,
  RotateCcw, Eye, Edit, Trash2, Share2, Bookmark
} from 'lucide-react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  Legend, ResponsiveContainer, BarChart, Bar, AreaChart, Area
} from 'recharts';

const Timeline = () => {
  const [selectedProject, setSelectedProject] = useState('all');
  const [viewMode, setViewMode] = useState('gantt');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState('month');

  // Comprehensive mock data
  const projects = [
    {
      id: 1,
      name: 'Riyadh Mall Construction',
      startDate: '2024-01-15',
      endDate: '2024-12-15',
      progress: 75,
      phases: [
        { name: 'Site Preparation', start: '2024-01-15', end: '2024-03-15', progress: 100, status: 'completed' },
        { name: 'Foundation', start: '2024-03-15', end: '2024-05-15', progress: 100, status: 'completed' },
        { name: 'Structure', start: '2024-05-15', end: '2024-08-15', progress: 80, status: 'in-progress' },
        { name: 'Interior', start: '2024-08-15', end: '2024-11-15', progress: 0, status: 'pending' },
        { name: 'Finishing', start: '2024-11-15', end: '2024-12-15', progress: 0, status: 'pending' }
      ]
    },
    {
      id: 2,
      name: 'Jeddah Office Complex',
      startDate: '2024-06-01',
      endDate: '2025-03-20',
      progress: 30,
      phases: [
        { name: 'Design Phase', start: '2024-06-01', end: '2024-08-31', progress: 60, status: 'in-progress' },
        { name: 'Permit Application', start: '2024-09-01', end: '2024-10-31', progress: 0, status: 'pending' },
        { name: 'Construction', start: '2024-11-01', end: '2025-02-28', progress: 0, status: 'pending' },
        { name: 'Handover', start: '2025-03-01', end: '2025-03-20', progress: 0, status: 'pending' }
      ]
    },
    {
      id: 3,
      name: 'Dammam Industrial Park',
      startDate: '2023-09-01',
      endDate: '2024-08-30',
      progress: 100,
      phases: [
        { name: 'Site Development', start: '2023-09-01', end: '2023-12-31', progress: 100, status: 'completed' },
        { name: 'Building Construction', start: '2024-01-01', end: '2024-05-31', progress: 100, status: 'completed' },
        { name: 'Equipment Installation', start: '2024-06-01', end: '2024-07-31', progress: 100, status: 'completed' },
        { name: 'Final Handover', start: '2024-08-01', end: '2024-08-30', progress: 100, status: 'completed' }
      ]
    },
    {
      id: 4,
      name: 'Abha Resort Development',
      startDate: '2024-01-01',
      endDate: '2025-01-15',
      progress: 60,
      phases: [
        { name: 'Site Development', start: '2024-01-01', end: '2024-03-31', progress: 100, status: 'completed' },
        { name: 'Main Structure', start: '2024-04-01', end: '2024-07-31', progress: 90, status: 'in-progress' },
        { name: 'Interior Work', start: '2024-08-01', end: '2024-11-30', progress: 0, status: 'pending' },
        { name: 'Landscaping', start: '2024-12-01', end: '2025-01-15', progress: 0, status: 'pending' }
      ]
    }
  ];

  const milestones = [
    { id: 1, projectId: 1, name: 'Foundation Complete', date: '2024-03-15', status: 'completed', description: 'All foundation work completed and inspected' },
    { id: 2, projectId: 1, name: 'Structure 50%', date: '2024-06-20', status: 'completed', description: 'Main structural elements 50% complete' },
    { id: 3, projectId: 1, name: 'Interior Finishing', date: '2024-10-30', status: 'pending', description: 'Begin interior finishing work' },
    { id: 4, projectId: 2, name: 'Design Approval', date: '2024-08-15', status: 'in-progress', description: 'Final design approval from stakeholders' },
    { id: 5, projectId: 2, name: 'Permit Application', date: '2024-09-30', status: 'pending', description: 'Submit all permit applications' },
    { id: 6, projectId: 3, name: 'Final Handover', date: '2024-08-30', status: 'completed', description: 'Project completed and handed over to client' },
    { id: 7, projectId: 4, name: 'Main Structure Complete', date: '2024-07-31', status: 'in-progress', description: 'Main building structure completed' }
  ];

  const timelineData = [
    { month: 'Jan', completed: 2, inProgress: 3, pending: 1, delayed: 0 },
    { month: 'Feb', completed: 3, inProgress: 4, pending: 2, delayed: 1 },
    { month: 'Mar', completed: 5, inProgress: 3, pending: 3, delayed: 1 },
    { month: 'Apr', completed: 6, inProgress: 4, pending: 2, delayed: 0 },
    { month: 'May', completed: 8, inProgress: 3, pending: 1, delayed: 0 },
    { month: 'Jun', completed: 10, inProgress: 2, pending: 2, delayed: 1 },
    { month: 'Jul', completed: 12, inProgress: 1, pending: 3, delayed: 1 },
    { month: 'Aug', completed: 15, inProgress: 0, pending: 2, delayed: 0 }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'in-progress': return 'bg-blue-500';
      case 'pending': return 'bg-gray-400';
      case 'delayed': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  const getStatusTextColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600';
      case 'in-progress': return 'text-blue-600';
      case 'pending': return 'text-gray-600';
      case 'delayed': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const calculateDaysBetween = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  };

  const GanttChart = () => (
    <div className="space-y-6">
      {projects.map((project) => (
        <motion.div
          key={project.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-6 rounded-2xl backdrop-blur-sm border ${
            isDarkMode 
              ? 'bg-white/10 border-white/20' 
              : 'bg-white/70 border-gray-200/50'
          } shadow-lg`}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {project.name}
              </h3>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {formatDate(project.startDate)} - {formatDate(project.endDate)}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDarkMode ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'
              }`}>
                {project.progress}% Complete
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {project.phases.map((phase, index) => (
              <div key={index} className="relative">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${getStatusColor(phase.status)}`}></div>
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {phase.name}
                    </span>
                  </div>
                  <div className="flex items-center space-x-4 text-xs">
                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                      {formatDate(phase.start)} - {formatDate(phase.end)}
                    </span>
                    <span className={`font-medium ${getStatusTextColor(phase.status)}`}>
                      {phase.progress}%
                    </span>
                  </div>
                </div>
                
                <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all duration-500 ${
                      phase.status === 'completed' ? 'bg-green-500' :
                      phase.status === 'in-progress' ? 'bg-blue-500' :
                      phase.status === 'delayed' ? 'bg-red-500' : 'bg-gray-400'
                    }`}
                    style={{ width: `${phase.progress}%` }}
                  />
                  {phase.status === 'in-progress' && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      ))}
    </div>
  );

  const MilestonesList = () => (
    <div className="space-y-4">
      {milestones.map((milestone) => (
        <motion.div
          key={milestone.id}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className={`p-4 rounded-xl backdrop-blur-sm border ${
            isDarkMode 
              ? 'bg-white/10 border-white/20' 
              : 'bg-white/70 border-gray-200/50'
          } shadow-lg hover:shadow-xl transition-all duration-300`}
        >
          <div className="flex items-start space-x-4">
            <div className={`p-2 rounded-lg ${getStatusColor(milestone.status)}`}>
              <Target className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {milestone.name}
                </h4>
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                  milestone.status === 'completed' ? 'bg-green-100 text-green-800' :
                  milestone.status === 'in-progress' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {milestone.status}
                </div>
              </div>
              <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {milestone.description}
              </p>
              <div className="flex items-center space-x-4 text-xs">
                <div className={`flex items-center space-x-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <Calendar className="w-3 h-3" />
                  <span>{formatDate(milestone.date)}</span>
                </div>
                <div className={`flex items-center space-x-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <Users className="w-3 h-3" />
                  <span>Team assigned</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );

  const TimelineChart = () => (
    <div className={`p-6 rounded-2xl backdrop-blur-sm border ${
      isDarkMode 
        ? 'bg-white/10 border-white/20' 
        : 'bg-white/70 border-gray-200/50'
    } shadow-lg`}>
      <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
        Timeline Overview
      </h3>
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={timelineData}>
          <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#E5E7EB'} />
          <XAxis 
            dataKey="month" 
            stroke={isDarkMode ? '#9CA3AF' : '#6B7280'} 
          />
          <YAxis stroke={isDarkMode ? '#9CA3AF' : '#6B7280'} />
          <Tooltip 
            contentStyle={{
              backgroundColor: isDarkMode ? '#1F2937' : '#FFFFFF',
              border: isDarkMode ? '1px solid #374151' : '1px solid #E5E7EB',
              borderRadius: '8px'
            }}
          />
          <Legend />
          <Area 
            type="monotone" 
            dataKey="completed" 
            stackId="1" 
            stroke="#10B981" 
            fill="#10B981" 
            fillOpacity={0.6} 
          />
          <Area 
            type="monotone" 
            dataKey="inProgress" 
            stackId="1" 
            stroke="#3B82F6" 
            fill="#3B82F6" 
            fillOpacity={0.6} 
          />
          <Area 
            type="monotone" 
            dataKey="pending" 
            stackId="1" 
            stroke="#6B7280" 
            fill="#6B7280" 
            fillOpacity={0.6} 
          />
          <Area 
            type="monotone" 
            dataKey="delayed" 
            stackId="1" 
            stroke="#EF4444" 
            fill="#EF4444" 
            fillOpacity={0.6} 
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );

  return (
    <div className="min-h-screen p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className={`text-4xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Timeline
            </h1>
            <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Interactive Gantt charts and milestone tracking
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl shadow-lg"
            >
              <Plus className="w-4 h-4" />
              <span>Add Milestone</span>
            </motion.button>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-6">
          {/* View Toggle */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode('gantt')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                viewMode === 'gantt'
                  ? isDarkMode
                    ? 'bg-indigo-500 text-white'
                    : 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white'
                  : isDarkMode
                    ? 'bg-white/10 text-gray-300 hover:bg-white/20'
                    : 'bg-white/80 text-gray-600 hover:bg-gray-100'
              }`}
            >
              <BarChart3 className="w-4 h-4 inline mr-2" />
              Gantt Chart
            </button>
            <button
              onClick={() => setViewMode('milestones')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                viewMode === 'milestones'
                  ? isDarkMode
                    ? 'bg-indigo-500 text-white'
                    : 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white'
                  : isDarkMode
                    ? 'bg-white/10 text-gray-300 hover:bg-white/20'
                    : 'bg-white/80 text-gray-600 hover:bg-gray-100'
              }`}
            >
              <List className="w-4 h-4 inline mr-2" />
              Milestones
            </button>
            <button
              onClick={() => setViewMode('overview')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                viewMode === 'overview'
                  ? isDarkMode
                    ? 'bg-indigo-500 text-white'
                    : 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white'
                  : isDarkMode
                    ? 'bg-white/10 text-gray-300 hover:bg-white/20'
                    : 'bg-white/80 text-gray-600 hover:bg-gray-100'
              }`}
            >
              <TrendingUp className="w-4 h-4 inline mr-2" />
              Overview
            </button>
          </div>

          {/* Timeframe Selector */}
          <div className="flex items-center space-x-2">
            {['week', 'month', 'quarter', 'year'].map((timeframe) => (
              <button
                key={timeframe}
                onClick={() => setSelectedTimeframe(timeframe)}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                  selectedTimeframe === timeframe
                    ? isDarkMode
                      ? 'bg-indigo-500 text-white'
                      : 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white'
                    : isDarkMode
                      ? 'bg-white/10 text-gray-300 hover:bg-white/20'
                      : 'bg-white/80 text-gray-600 hover:bg-gray-100'
                }`}
              >
                {timeframe.charAt(0).toUpperCase() + timeframe.slice(1)}
              </button>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            <button className={`p-2 rounded-lg transition-all ${
              isDarkMode 
                ? 'hover:bg-white/10 text-gray-300' 
                : 'hover:bg-gray-100 text-gray-600'
            }`}>
              <Download className="w-4 h-4" />
            </button>
            <button className={`p-2 rounded-lg transition-all ${
              isDarkMode 
                ? 'hover:bg-white/10 text-gray-300' 
                : 'hover:bg-gray-100 text-gray-600'
            }`}>
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>

      {/* Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={viewMode}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          {viewMode === 'gantt' && <GanttChart />}
          {viewMode === 'milestones' && <MilestonesList />}
          {viewMode === 'overview' && <TimelineChart />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default Timeline; 